import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {BackofficePermissionsService} from '../_shared/backoffice-permissions.service';
import {RolesService} from '../_shared/roles-service';
import {BackofficeService, ServiceService} from '../_services/configuration-services';
import {PermissionKeys} from '../app.constants';

/**
 * Allows access to a service edit page if:
 *   - the user is SuperAdmin, OR
 *   - the user has the `gestionServices` permission (admin), OR
 *   - the user is registered as an exploitant of the service (ServiceBackoffices).
 *
 * Backend authority: ServiceController + AuthorizeExploitantMiddleware enforces the same rule.
 * Route must expose the service id under the `serviceId` route param.
 */
export const ServiceEditGuard: CanActivateFn = async (route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> => {
    const permissions = inject(BackofficePermissionsService);
    const rolesService = inject(RolesService);
    const router = inject(Router);
    const backofficeService = inject(BackofficeService);
    const serviceService = inject(ServiceService);

    if (rolesService.hasRoleSuperAdmin()) {
        return true;
    }

    // Permissions may not be loaded yet on a hard refresh (initialNavigation: enabledBlocking).
    // Mirror the bootstrap logic from PermissionGuard: non-backoffice accounts are denied immediately,
    // backoffice accounts trigger getMe() to populate the permission cache and the backoffice id.
    if (!permissions.permissionsLoaded$.value) {
        if (!rolesService.isBackofficeAccount()) {
            return router.createUrlTree(['/services']);
        }
        const me = await firstValueFrom(backofficeService.getMe());
        permissions.setCurrentBackofficeId(me.id);
        permissions.setPermissions((me.permissions ?? []).map(p => p.key));
    }

    if (permissions.hasPermission(PermissionKeys.gestionServices)) {
        return true;
    }

    const serviceId = route.paramMap.get('serviceId');
    // Backoffice.Id (database id), NOT RolesService.getAccountId() which returns the ADB2C
    // object id from the token — ServiceDto.backoffices[*].id holds the database id.
    const backofficeId = permissions.getCurrentBackofficeId();
    if (!serviceId || !backofficeId) {
        return router.createUrlTree(['/services']);
    }

    const service = await firstValueFrom(serviceService.getService(serviceId));
    const isExploitant = service.backoffices?.some(b => b.id === backofficeId) ?? false;
    return isExploitant ? true : router.createUrlTree(['/services']);
};
